import React, {useState, useRef, useEffect} from 'react';
import { useDesktopAutofocus } from '../hooks/useDesktopAutofocus';
import { loadSessionState, saveSessionState } from '../hooks/useSessionListState';
import { Container, Row, Col, Accordion, AccordionBody, AccordionHeader, AccordionItem } from 'reactstrap';
import { Link } from 'react-router-dom';
import { getUnits } from '../shared/unitInfo';
import PageMeta from './PageMeta';
import '../App.css';
import PagePagination from './PagePagination';
import { scrollResultsIntoView } from './scrollResultsIntoView';
import SkeletonImage from './SkeletonImage';
import EmptyResults from './EmptyResults';
import SearchInput from './SearchInput';
import TagFilterPanel from './tags/TagFilterPanel';
import { entityMatchesSelectedTags } from '../shared/tags/tagDictionary';

const DEFAULT_UNIT_FILTERS = {
    attribute: {
        Fire: true,
        Water: true,
        Earth: true,
        Light: true,
        Dark: true
    },
    type: {
        Machine: true,
        Dragon: true,
        Giant: true,
        Spirit: true,
        Beast: true,
        God: true,
        Human: true,
        Demon: true
    },
    server: {
        Global: true,
        Japan: true
    },
    showOnly: {
        ascended: false
    }
};

const UNITS_LIST_STATE_KEY = 'gs-units-list-state';

const DEFAULT_UNITS_LIST_STATE = {
    query: '',
    filters: DEFAULT_UNIT_FILTERS,
    selectedTags: [],
    tagMatchMode: 'or',
    sortOrder: 'default',
    unitsPerPage: 100,
    currentPage: 1,
};

export default function Units () {
    //Obtain Unit Data
    const units = getUnits();

    const [savedListState] = useState(() =>
        loadSessionState(UNITS_LIST_STATE_KEY, DEFAULT_UNITS_LIST_STATE)
    );

    //Search for Unit Names
    const [query, setQuery] = useState(savedListState.query);

    //Filter based on Unit Types
    const [filters, setFilters] = useState(savedListState.filters);

    const [selectedTags, setSelectedTags] = useState(savedListState.selectedTags || []);
    const [tagMatchMode, setTagMatchMode] = useState(savedListState.tagMatchMode === 'and' ? 'and' : 'or');

    //Accordion
    const [accordion, setAccordion] = useState("0")
    const toggle = (id) => {
        accordion === id ? setAccordion() : setAccordion(id)
    }
    

    //Function to Set Button Filters
    const handleChange = (event) => {
        const { name, value, checked } = event.target;
        setFilters({
            ...filters,
            [name]: {
            ...filters[name],
            [value]: checked,
            },
        });

        setCurrentPage(1)
    };

    //Sort Options
    const [sortOrder, setSortOrder] = useState(savedListState.sortOrder)

    //Default amount of units per page
    const [unitsPerPage, setUnitsPerPage] = useState(savedListState.unitsPerPage); 
    const [currentPage, setCurrentPage] = useState(savedListState.currentPage);

    useEffect(() => {
        saveSessionState(UNITS_LIST_STATE_KEY, {
            query,
            filters,
            selectedTags,
            tagMatchMode,
            sortOrder,
            unitsPerPage,
            currentPage,
        });
    }, [query, filters, selectedTags, tagMatchMode, sortOrder, unitsPerPage, currentPage]);

    const clearAdvancedFilters = () => {
        setFilters(DEFAULT_UNIT_FILTERS);
        setSelectedTags([]);
        setTagMatchMode('or');
        setCurrentPage(1);
    };

    const advancedFiltersAtDefault =
        JSON.stringify(filters) === JSON.stringify(DEFAULT_UNIT_FILTERS)
        && selectedTags.length === 0
        && tagMatchMode === 'or';

    const clearFilters = () => {
        setFilters(DEFAULT_UNIT_FILTERS);
        setSelectedTags([]);
        setTagMatchMode('or');
        setQuery("");
        setSortOrder("default");
        setCurrentPage(1);
    };

    const handleSelectedTagsChange = (next) => {
        setSelectedTags(next);
        setCurrentPage(1);
    };

    const handleTagMatchModeChange = (mode) => {
        setTagMatchMode(mode);
        setCurrentPage(1);
    };

    //Display Unit Thumbnails
    return (
        <React.Fragment>
            <Container className="gs-page">
                <PageMeta
                    title="Units"
                    description="Browse the full Grand Summoners unit database with stats, skills, lore, and tier rankings."
                    path="/units"
                />
                <div className="gs-page-gutter">
                    <header className="gs-page-intro gs-page-intro--with-action">
                        <div className="gs-page-intro__copy">
                            <h3>Select a Unit to view the Unit's information.</h3>
                            <p>Please be aware that ALL units are available, including units currently unreleased on Global.</p>
                        </div>
                        <Link to="/compare" className="gs-btn gs-page-intro__action">
                            Compare
                        </Link>
                    </header>

                    <div className="gs-controls-panel">
                        <RenderSearch query={query} setQuery={setQuery} setCurrentPage={setCurrentPage} />
                        <div className="gs-options">
                            <RenderSortOptions sortOrder={sortOrder} setSortOrder={setSortOrder} />
                            <RenderShowOnlyFilters filters={filters} handleChange={handleChange} />
                            <RenderUnitAmount units={units} setUnitsPerPage={setUnitsPerPage} unitsPerPage={unitsPerPage} setCurrentPage={setCurrentPage} />
                        </div>
                    </div>

                    <div style={{marginBottom: "var(--gs-space-5)"}}>
                        <Accordion open={accordion} toggle={toggle}>
                            <AccordionItem style={{ borderColor: "var(--gs-border)" }}>
                                <AccordionHeader targetId="1">
                                    <span className="gs-accordion-title">
                                        Advanced Filters
                                    </span>
                                </AccordionHeader>
                                <AccordionBody accordionId="1" style={{ backgroundColor: "var(--gs-bg)", color: "var(--gs-text)", padding: "0.5rem" }}>
                                    <div className="d-flex justify-content-end mb-2">
                                        <button type="button" className="gs-btn gs-btn--danger" onClick={clearAdvancedFilters} disabled={advancedFiltersAtDefault}>
                                            Clear All
                                        </button>
                                    </div>
                                    <Row className="g-2">
                                        <Col xs="12" sm="4">
                                            <ServerFilters filters={filters} handleChange={handleChange} />
                                        </Col>
                                        <Col xs="12" sm="4">
                                            <AttributeFilters filters={filters} handleChange={handleChange} />
                                        </Col>
                                        <Col xs="12" sm="4">
                                            <TypeFilters filters={filters} handleChange={handleChange} />
                                        </Col>
                                        <Col xs="12">
                                            <TagFilterPanel
                                                appliesTo="unit"
                                                selectedTags={selectedTags}
                                                onChange={handleSelectedTagsChange}
                                                onClear={() => handleSelectedTagsChange([])}
                                                matchMode={tagMatchMode}
                                                onMatchModeChange={handleTagMatchModeChange}
                                            />
                                        </Col>
                                    </Row>
                                </AccordionBody>
                            </AccordionItem>
                        </Accordion>
                    </div>

                    <RenderUnits
                        units={units}
                        filters={filters}
                        selectedTags={selectedTags}
                        tagMatchMode={tagMatchMode}
                        query={query}
                        sortOrder={sortOrder}
                        setUnitsPerPage={setUnitsPerPage}
                        unitsPerPage={unitsPerPage}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        onClearFilters={clearFilters}
                    />
                </div>
            </Container>
        </React.Fragment>
    );
}

const RenderUnits = ({ units, filters, selectedTags, tagMatchMode, query, sortOrder, unitsPerPage, currentPage, setCurrentPage, onClearFilters }) => {
    const filteredUnits = units
    .filter(unit => {
        const isGlobalChecked = filters.server.Global;
        const isJapanChecked = filters.server.Japan;

        //Check for Server
        const hasMatchingServer = (isGlobalChecked && isJapanChecked)
        || ((isGlobalChecked && !isJapanChecked) && !unit.lore.evoawk?.toLowerCase().includes('unreleased in global.') && !unit.name.toLowerCase().includes('kazlaser'))
        || ((isJapanChecked && !isGlobalChecked) && (unit.lore.evoawk?.toLowerCase().includes('unreleased in global.') || unit.lore.evo5?.toLowerCase().includes('unreleased in global.')) );

        //Check for Attributes
        const selectedAttr = Object.keys(filters.attribute).filter((key) => filters.attribute[key])
        const hasMatchingAttr = selectedAttr.some((attr) => unit.attribute.includes(attr))

        //Check for Types
        const selectedTypes = Object.keys(filters.type).filter((key) => filters.type[key])
        const hasMatchingTypes = selectedTypes.some((type) => unit.type.includes(type))

        //Check for Search
        const hasMatchingName = unit.name.toLowerCase().includes(query.toLowerCase());

        const hasMatchingTags = entityMatchesSelectedTags(unit.tags, selectedTags, tagMatchMode);

        if(filters.showOnly.ascended) {
            const hasAscended = unit.image.detailsuper !== undefined
            return hasMatchingAttr && hasMatchingTypes && hasMatchingName && hasMatchingServer && hasMatchingTags && hasAscended
        }
        return hasMatchingAttr && hasMatchingTypes && hasMatchingName && hasMatchingServer && hasMatchingTags
    })
    .sort((a, b) => {
        //If Attribute Order is checked
        if (sortOrder === "element") {
            const attrOrder = ['Fire', 'Water', 'Earth', 'Light', 'Dark']
            return attrOrder.indexOf(a.attribute) - attrOrder.indexOf(b.attribute)
        }
        
        //Default Sort Order
        return a.id - b.id
    })

    const totalUnits = filteredUnits.length;
    const perPage = Number(unitsPerPage) || 50;
    const totalPages = Math.max(1, Math.ceil(totalUnits / perPage));
    const safePage = Math.min(Math.max(1, Number(currentPage) || 1), totalPages);
    const resultsRef = useRef(null);
    const shouldScrollRef = useRef(false);

    function handlePageChange(pageNumber) {
        shouldScrollRef.current = true;
        setCurrentPage(pageNumber);
    }

    useEffect(() => {
        if (!shouldScrollRef.current) {
            return;
        }
        shouldScrollRef.current = false;
        scrollResultsIntoView(resultsRef.current);
    }, [safePage]);

    const indexOfLastUnit = safePage * perPage;
    const indexOfFirstUnit = indexOfLastUnit - perPage;
    const unitsToShow = filteredUnits.slice(indexOfFirstUnit, indexOfLastUnit);

    if (totalUnits === 0) {
        return (
            <EmptyResults
                title="No units match your filters"
                message="Try adjusting search or filters, or clear them to see the full unit list."
                onClear={onClearFilters}
            />
        );
    }

    return (
        <div className="gs-results-pane" ref={resultsRef} style={{marginTop: "var(--gs-space-2)"}}>
            <PagePagination
                currentPage={safePage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                ariaLabel="Unit Pages"
            />
            <div className="gs-thumb-grid is-paging" key={safePage}>
            {
            unitsToShow
            .map(unit => {
                if(unit.image.thumbsuper){
                    return (
                        <RenderThumbnail thumbnail={unit.image.thumbsuper} name={unit.name} key={unit.id} />
                    );
                }
                else if(unit.image.thumbawk){
                    return (
                        <RenderThumbnail thumbnail={unit.image.thumbawk} name={unit.name} key={unit.id} />
                    );
                }
                else {
                    return (
                        <RenderThumbnail thumbnail={unit.image.thumb5} name={unit.name} key={unit.id} />
                    );
                }
            })}
            </div>
            <div style={{ marginTop: "var(--gs-space-4)"}}>
            <PagePagination
                currentPage={safePage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                ariaLabel="Unit Pages"
                showMeta={false}
            />
            </div>
        </div>
    )
}

const RenderSearch = ({ query, setQuery, setCurrentPage }) => {
    const searchRef = useDesktopAutofocus();

    const handleSearch = (e) => {
        setQuery(e.target.value)
        setCurrentPage(1)
    }

    return (
        <SearchInput
            inputRef={searchRef}
            placeholder="Search Unit Name"
            value={query}
            onChange={handleSearch}
            onClear={() => {
                setQuery("")
                setCurrentPage(1)
            }}
        />
    )
}

const RenderSortOptions = ({ sortOrder, setSortOrder }) => {
    return (
        <div className="gs-field">
            <span className="gs-field-label">Sort By</span>
            <div className="gs-seg">
                <label className={sortOrder === "default" ? "is-active" : ""}>
                    <input type="radio"
                            value="default"
                            checked={ sortOrder === "default" }
                            onChange={() => setSortOrder("default")} 
                            />
                    ID
                </label>
                <label className={sortOrder === "element" ? "is-active" : ""}>
                    <input type="radio"
                            value="element"
                            checked={ sortOrder === "element" }
                            onChange={() => setSortOrder("element")} />
                    Attribute
                </label>
            </div>
        </div>
    )
}

const RenderUnitAmount = ({ setUnitsPerPage, unitsPerPage, units, setCurrentPage }) => {
    const handleSelectChange = (e) => {
        setUnitsPerPage(e.target.value)
        setCurrentPage(1)
    }

    return (
        <div className="gs-field">
            <span className="gs-field-label">Show</span>
            <select className="gs-select" value={unitsPerPage} onChange={handleSelectChange}>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value={units.length}>All</option>
            </select>
        </div>
    )
}

const RenderThumbnail = ({ thumbnail, name }) => {
    return (
        <div className="gs-thumb-card">
            <Link to={`/units/${name}`}>
                <SkeletonImage src={thumbnail} alt={name} />
                <span className="gs-thumb-name">{name}</span>
            </Link>
        </div>
    )
}

const ServerFilters = ({ filters, handleChange }) => {
    return (
        <div className="gs-filter-group">
            <span className="gs-field-label">Server</span>
            <form className="filterButtons">
                <label className={filters.server.Global ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="server"
                        value="Global"
                        checked={filters.server.Global}
                        onChange={handleChange} 
                        
                        />
                        Global
                </label>
                <label className={filters.server.Japan ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="server"
                        value="Japan"
                        checked={filters.server.Japan}
                        onChange={handleChange}
                        />
                        Unreleased
                </label>
            </form>
        </div>
    )
}

const TypeFilters = ({ filters, handleChange }) => {
    return (
        <div className="gs-filter-group">
            <span className="gs-field-label">Race</span>
            <form className="filterButtons">
                <label className={filters.type.Human ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="type"
                        value="Human"
                        checked={filters.type.Human}
                        onChange={handleChange}
                        />
                    <img src="/db/Icons/types/Human_Icon.webp" alt="Human Type" className="filterIcons" /> 
                    Human
                </label>
                <label className={filters.type.Demon ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="type"
                        value="Demon"
                        checked={filters.type.Demon}
                        onChange={handleChange}
                        />
                    <img src="/db/Icons/types/Demon_Icon.webp" alt="Demon Type" className="filterIcons" /> 
                    Demon
                </label>
                <label className={filters.type.God ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="type"
                        value="God"
                        checked={filters.type.God}
                        onChange={handleChange}
                        />
                    <img src="/db/Icons/types/God_Icon.webp" alt="God Type" className="filterIcons" /> 
                    God
                </label>
                <label className={filters.type.Spirit ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="type"
                        value="Spirit"
                        checked={filters.type.Spirit}
                        onChange={handleChange}
                        />
                    <img src="/db/Icons/types/Spirit_Icon.webp" alt="Spirit Type" className="filterIcons" /> 
                    Spirit
                </label>
                <label className={filters.type.Machine ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="type"
                        value="Machine"
                        checked={filters.type.Machine}
                        onChange={handleChange}
                        />
                    <img src="/db/Icons/types/Machine_Icon.webp" alt="Machine Type" className="filterIcons" /> 
                    Machine
                </label>
                <label className={filters.type.Dragon ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="type"
                        value="Dragon"
                        checked={filters.type.Dragon}
                        onChange={handleChange}
                        />
                    <img src="/db/Icons/types/Dragon_Icon.webp" alt="Dragon Type" className="filterIcons" /> 
                    Dragon
                </label>
                <label className={filters.type.Beast ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="type"
                        value="Beast"
                        checked={filters.type.Beast}
                        onChange={handleChange}
                        />
                    <img src="/db/Icons/types/Beast_Icon.webp" alt="Beast Type" className="filterIcons" /> 
                    Beast
                </label>
                <label className={filters.type.Giant ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="type"
                        value="Giant"
                        checked={filters.type.Giant}
                        onChange={handleChange}
                        />
                        <img src="/db/Icons/types/Giant_Icon.webp" alt="Giant Type" className="filterIcons" /> 
                        Giant
                </label>
            </form>
        </div>
    )
}

const AttributeFilters = ({ filters, handleChange }) => {
    return (
        <div className="gs-filter-group">
            <span className="gs-field-label">Attributes</span>
            <form className="filterButtons">
                <label className={filters.attribute.Fire ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="attribute"
                        value="Fire"
                        checked={filters.attribute.Fire}
                        onChange={handleChange}
                    />
                    <img src="/db/Icons/types/Fire_Icon.webp" alt="Fire Attribute" className="filterIcons" /> 
                    Fire
                </label>
                <label className={filters.attribute.Water ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="attribute"
                        value="Water"
                        checked={filters.attribute.Water}
                        onChange={handleChange}
                    />
                    <img src="/db/Icons/types/Water_Icon.webp" alt="Water Attribute" className="filterIcons" /> 
                    Water
                </label>
                <label className={filters.attribute.Earth ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="attribute"
                        value="Earth"
                        checked={filters.attribute.Earth}
                        onChange={handleChange}
                    />
                    <img src="/db/Icons/types/Earth_Icon.webp" alt="Earth Attribute" className="filterIcons" /> 
                    Earth
                </label>
                <label className={filters.attribute.Light ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="attribute"
                        value="Light"
                        checked={filters.attribute.Light}
                        onChange={handleChange}
                    />
                    <img src="/db/Icons/types/Light_Icon.webp" alt="Light Attribute" className="filterIcons" /> 
                    Light
                </label>
                <label className={filters.attribute.Dark ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="attribute"
                        value="Dark"
                        checked={filters.attribute.Dark}
                        onChange={handleChange}
                    />
                    <img src="/db/Icons/types/Dark_Icon.webp" alt="Dark Attribute" className="filterIcons" /> 
                    Dark
                </label>
            </form>
        </div>
    )
}

const RenderShowOnlyFilters = ({ filters, handleChange }) => {
    return (
        <div className="gs-field">
            <span className="gs-field-label">Show Only</span>
            <div className="gs-seg">
                <label className={filters.showOnly.ascended ? "is-active" : ""}>
                    <input type="checkbox"
                            name="showOnly"
                            value="ascended"
                            checked={ filters.showOnly.ascended }
                            onChange={handleChange} />
                    Ascended
                </label>
            </div>
        </div>
    )
}